// pageContext.js — page awareness for the GENERAL workspace AI.
//
// buildPageContext(root) inspects the live workspace state (the state-root
// object held at el.state.root) and returns a COMPACT, model-readable snapshot
// of the page the user is currently viewing: { currentPage, pageData,
// pageActions }. simoneAiTransport merges it into the per-turn dispatch context
// for the GENERAL scope only (the add-app builder is extension-scoped and gets
// none). The server surfaces it in the agent's system instruction so the AI can
// answer about — and act on — what the user is looking at.
//
// selectPageItem(type, id, collection?) is the el.call companion (registered
// via functions/index.js) the AI's `select_page_item` client tool invokes to
// open a specific item (a ticket, channel, document, event, room — or a
// workspace RECORD, on the app page that owns its collection). It only flips
// client selection state / opens a modal or page — it never writes data (data
// mutations route through the proper SDK paths, not the page AI).

// Bare shared-library import, not a deep workspacePrefix.js subpath — see
// app.js's identical fix note (tickets/catalog-fixall.md P0).
// The `.functions` read is DEFERRED to call time: this module loads inside
// the shared-barrel init cycle (shared components → IdentityAdminComponents
// → shell AdminChrome → shell functions barrel → here), where the barrel's
// default export is still undefined — a module-scope destructure crashed
// the entire shell boot (blank app, no spiral removal, 2026-08-12).
import _wsShared from '@symbo-ls/workspace-shared'
import {
  shellStripWorkspacePrefix,
  shellParseWorkspaceProjectPath,
  shellSay
} from './shellSharedDelegates.js'
import { slotFullscreenActive } from './workspaceProjects.js'
// The SAME normalizer the send path runs, so an awareness chip's "sent" verdict
// and what actually leaves the client are one decision, not two.
import { normalizeComposerImages } from './_simone/attachments.js'
import { readAiAttached } from './aiAttached.js'
// READ-ONLY use of the module record-route registry (S1/S3 contract,
// functions/moduleRecordRoutes.js): a declared record route names a module
// page, which is what lets the FOCUS route chip prefer the module's own
// title over a segment-cased path. Nothing here writes to it.
import { moduleRecordRoute, moduleRecordRoutes } from './moduleRecordRoutes.js'
import { activeWorkspaceId } from './scopedClient.js'
import { installedAppRefs } from './workspaceConfigs.js'
// A server doc body (markdown string, or a legacy JSON blocks string) in the
// shape the documents surface carries — the same reader its own fetches use.
import { _docContent } from '@symbo-ls/workspace-documents/functions/parseBody.js'
// The chips' WORDS: the route → bundle-key table, the collection → phrase-key
// table, and the two resolvers that read them. `PAGES[].page` stays the
// MODEL's English name for a route; no chip reads it (aiAwarenessLabels.js).
import { routeLabel, rowsSummary, warnUnlabelledOnce } from './aiAwarenessLabels.js'
// An activity event's own words: the ONE reader the History card and the
// /admin/logs row read too (functions/history.js). Imported, not `el.call`ed —
// the model wire builds this context with no element (aiAssistant.js and
// simoneAiTransport.js call `buildPageContext(root)` bare).
import { historyEventWords } from './history.js'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)
// EntityRef helpers (shared/functions/entityRef.js) — deferred to call time
// for the same barrel-init-cycle reason as the three handles above.
const formatEntityRef = (...args) => _wsShared.functions.formatEntityRef(...args)
const entityRefIcon = (...args) => _wsShared.functions.entityRefIcon(...args)
const entityRefKindKey = (...args) => _wsShared.functions.entityRefKindKey(...args)
const i18nStaticEnglish = () => _wsShared.functions.i18nStaticEnglish()
// A bundle sentence for a chip or a page name: through the element's polyglot
// when there is one (every `el.call(...)` caller), the SHIPPED ENGLISH value on
// the headless path (aiAssistant.js's bare buildPageContext call — the model
// wire, which must never carry a raw key).
const cap = (arr, n = 15) => (Array.isArray(arr) ? arr.slice(0, n) : [])
const str = (v) => (v == null ? '' : String(v))
// People/assignee fields are sometimes a populated object, sometimes a bare
// string/id — never stringify an object into "[object Object]".
const nameOf = (v) => (v && typeof v === 'object' ? str(v.name || v.fullName || v.email) : str(v))

// Resolve the effective route the user is viewing; when on the home dashboard
// ('/') with a sub-app opened in the AppWindow (PageSlot), selectedPage is
// what's actually visible.
// THE ROUTE SIGNAL FIRST: `root.currentPath` — AppShell's history patch
// (shared AppShell.js installAppShellSync) writes it on every push, replace
// and popstate, so it IS the browser path — and reading it subscribes. A
// reactive prop that resolves the surface through here (the composer chip's
// "Context: …", aiScope.js) re-runs on an SPA navigation; reading
// `window.location` first, it never did: after Studio → Home by a click the
// chip kept "Context: Apps" until a reload (Fly QA 2026-10-18, D2). The
// browser path stays the fallback for a root that has no route (a headless
// caller, a test).
function effectivePath(root) {
  const routePath = root && typeof root.currentPath === 'string' ? root.currentPath : ''
  const winPath =
    routePath ||
    (typeof window !== 'undefined' && window.location && window.location.pathname) ||
    ''
  // Normalize past any `/w/<org-slug>` prefix so the intranet home ('/') check
  // + every downstream PAGES / PAGE_SUGGESTIONS / titleFromPath test resolves
  // correctly under multi-workspace tabs (§6d). No-op until Phase 3 emits the
  // prefix. `_canvasSlot` is an already-unprefixed internal route key.
  let path = shellStripWorkspacePrefix(winPath || '/')
  if ((!path || path === '/') && root?._canvasSlot) path = root._canvasSlot
  return path || '/'
}

// The documents editor's routes: '/notes/editor' and the legacy
// '/documents/editor' (pages/main.js PageSlot map, pages/index.js routes).
const EDITOR_ROUTE = /^\/(?:notes|documents)\/editor(?:[/?#]|$)/
// Is the editor ON SCREEN — as the page, in the PageSlot pane or in the
// split's second pane? Leaving the editor keeps `editorDoc` (documents
// pages/editor.js reuses it on the next open), so the key alone is not an
// open note (wave-2 (5), dev 2026-10-28: a closed note kept its chip).
const editorOnScreen = (root) =>
  [effectivePath(root), root._canvasSlot, root._canvasSlotSecond].some(
    (p) => typeof p === 'string' && EDITOR_ROUTE.test(p)
  )

// Per-route descriptors. `data(root)` returns a compact plain object (no
// functions, bounded arrays); `actions` lists what the AI can do here, phrased
// to name the exact tool + arg shape so the model calls it correctly.
const PAGES = [
  {
    test: (p) => p.startsWith('/tasks') || p.startsWith('/task/') || p.startsWith('/tickets'),
    page: 'Tasks',
    data: (root) => {
      // The board column carries the human label (`label`); a ticket's status is
      // the label of the column it sits in (linked by `columnKey`), falling back
      // to the raw `state`. Assignee is an email / login, not an object.
      const cols = Array.isArray(root.columns) ? root.columns : []
      const labelByKey = new Map(
        cols.map((c) => [str(c?.key), str(c?.label || c?.name || c?.title)])
      )
      const labelById = new Map(cols.map((c) => [str(c?.id), str(c?.label || c?.name || c?.title)]))
      const statusOf = (t) =>
        labelByKey.get(str(t?.columnKey || t?.column_key)) ||
        labelById.get(str(t?.columnId)) ||
        str(t?.state || t?.status)
      const assigneeOf = (t) =>
        nameOf(t?.assignee) ||
        str(
          t?.assigneeEmail ||
            t?.assignee_email ||
            (Array.isArray(t?.assigneeLogins) ? t.assigneeLogins[0] : '')
        )
      return {
        columns: cap(root.columns).map((c) => ({
          id: str(c?.id),
          name: str(c?.label || c?.name || c?.title)
        })),
        tickets: cap(root.tickets, 25).map((t) => ({
          id: str(t?.id),
          number: t?.number != null ? t.number : undefined,
          title: str(t?.title || t?.name),
          status: statusOf(t),
          assignee: assigneeOf(t)
        })),
        selectedTicket: root.selectedTicket
          ? { id: str(root.selectedTicket.id), title: str(root.selectedTicket.title) }
          : null
      }
    },
    actions: [
      "open a ticket (select_page_item type:'ticket' id:<ticketId>)",
      'move a ticket to another column / change its status (move_ticket ticket:<id|title> column:<name>)',
      'create a ticket (create_ticket); delete a ticket (delete_ticket)',
      'search the workspace (open_search)'
    ]
  },
  {
    test: (p) => p.startsWith('/chat'),
    page: 'Chat',
    data: (root) => ({
      channels: cap(root.channels, 30).map((c) => ({
        id: str(c?.id),
        name: str(c?.name || c?.label || c?.title)
      })),
      activeChannelId: str(root.activeChannelId) || null
    }),
    actions: [
      "switch to a channel (select_page_item type:'channel' id:<channelId>)",
      'send a message (send_message channel:<id|name> message:<text>); create a channel (create_channel)',
      'search the workspace (open_search)'
    ]
  },
  {
    test: (p) => p.startsWith('/calendar'),
    page: 'Calendar',
    data: (root) => ({
      // Shell root stores calendar events under `events` (state-app.js), matching
      // selectPageItem's event case + the shell calendar page — NOT the
      // standalone `calendarEvents` key. Reading calendarEvents here left the AI's
      // calendar context silently empty.
      events: cap(root.events, 30).map((e) => ({
        id: str(e?.id),
        title: str(e?.title || e?.name),
        start: str(e?.start || e?.date)
      })),
      selectedEvent: root.selectedEvent
        ? { id: str(root.selectedEvent.id), title: str(root.selectedEvent.title) }
        : null
    }),
    actions: [
      "open an event (select_page_item type:'event' id:<eventId>)",
      'navigate to another section (navigate_to_app)'
    ]
  },
  {
    // The built-in History app: the workspace activity log as a timeline
    // (pages/history.js). The rows are the audit log the page holds
    // (`historyEvents`, newest first); the two filters say what the person is
    // looking at.
    test: (p) => p.startsWith('/history'),
    page: 'History',
    data: (root) => ({
      activity: cap(root.historyEvents, 30).map((e) => ({
        id: str(e?.id),
        title: str(historyEventWords(e)),
        who: str(e?.user_email),
        type: str(e?.activity_type),
        at: str(e?.created_at)
      })),
      person: str(root.historyPerson) || null,
      activityType: str(root.historyType) || null
    }),
    actions: ['navigate to another section (navigate_to_app)']
  },
  {
    // Terminal — the App Store app system/terminal-app (apps/terminal; a
    // built-in until 2026-10-21). Its sessions live in the window's own
    // state, not on root, and the stub shell runs nothing, so the page tells
    // the model where the person is and carries no rows.
    test: (p) => p.startsWith('/terminal'),
    page: 'Terminal',
    data: () => ({}),
    actions: ['navigate to another section (navigate_to_app)']
  },
  {
    test: (p) => p.startsWith('/notes') || p.startsWith('/documents') || p.startsWith('/docs'),
    page: 'Notes',
    data: (root) => ({
      documents: cap(root.documents, 30).map((d) => ({
        id: str(d?.id),
        title: str(d?.title || d?.name || d?.label)
      })),
      notes: cap(root.notes, 30).map((n) => ({ id: str(n?.id), title: str(n?.title || n?.name) })),
      selectedFolder: str(root.selectedFolder) || null,
      selectedDocument: root.selectedDocument
        ? {
            id: str(root.selectedDocument.id),
            title: str(root.selectedDocument.title || root.selectedDocument.name)
          }
        : null
    }),
    actions: [
      "open a document (select_page_item type:'document' id:<docId>)",
      'create a note (create_note title:<t> content:<c>); rename (rename_note); delete (delete_note)',
      'search the workspace (open_search)'
    ]
  },
  {
    test: (p) => p.startsWith('/files') || p.startsWith('/infra'),
    page: 'Files',
    // `files` below is cut from `root.fileNodes` (40 at most). The rows chip
    // counts the WHOLE list there, so a desktop past the cap reads truncated.
    rootKeys: { files: 'fileNodes' },
    data: (root) => {
      // A file's CONTENT is what people actually ask about — "summarise the
      // docx", "what are the totals in the spreadsheet". This slice used to
      // carry name/kind only, so those prompts could be answered only by
      // guessing from the filename.
      //
      // The desktop already HAS the text: the office/markdown reader extracts
      // it into `_filePreviews[id]` to draw each tile's snippet, so it is
      // sitting in memory on this very page. Passing a bounded slice of it
      // costs nothing extra to produce. (No `mime` — the extension in `name`
      // already tells the model the format, and 40 × a 70-char OOXML mime
      // string is pure bulk.)
      //
      // Budgeted twice over: per file, so one long document can't crowd out
      // every other file, and in total, so a 40-icon desktop can't blow the
      // dispatch context. A file can end up with no `content` two ways — its
      // preview hasn't resolved (unreadable container, still loading, a format
      // with no text) or the budget ran out, which is flagged explicitly as
      // `contentOmitted`. The actions list below tells the model to say so
      // rather than guess from the filename; that distinction is the whole
      // difference between a correct answer and an invented one.
      const PER_FILE = 400
      const TOTAL = 4000
      let spent = 0
      let omitted = 0
      const files = cap(root.fileNodes, 40).map((n) => {
        const out = { id: str(n?.id), name: str(n?.name), kind: str(n?.kind) }
        if (typeof n?.size === 'number') out.size = n.size
        const preview = str((root._filePreviews || {})[n?.id]).trim()
        if (!preview) return out
        if (spent >= TOTAL) {
          omitted++
          out.contentOmitted = true
          return out
        }
        const slice = preview.slice(0, Math.min(PER_FILE, TOTAL - spent))
        spent += slice.length
        out.content = slice
        if (slice.length < preview.length) out.contentTruncated = true
        return out
      })
      return {
        files,
        currentFolder: str(root.filesOpenFolder) || null,
        // Never let the model treat a budget cut as "this file is empty".
        ...(omitted
          ? { note: omitted + ' more file(s) have contents that did not fit this context' }
          : {})
      }
    },
    actions: [
      "answer questions about a file's contents from its `content` field (extracted text — documents, spreadsheets, decks, notes)",
      'a file with no `content` field: its text was NOT provided — say the contents are unavailable, never guess them from the filename',
      'create a folder (create_folder name:<n>)',
      'rename (rename_file file:<id|name> newName:<n>); duplicate (duplicate_file file:<id|name> newName:<n>)',
      'delete (delete_file file:<id|name>); move into a folder (move_file file:<id|name> folder:<id|name|desktop>)',
      'search the workspace (open_search)'
    ]
  },
  {
    test: (p) => p.startsWith('/meet'),
    page: 'Meet',
    data: (root) => ({
      rooms: cap(root.rooms, 30).map((r) => ({
        id: str(r?.id),
        name: str(r?.name || r?.label || r?.title)
      })),
      activeRoomId: str(root.activeRoomId) || null
    }),
    actions: [
      "open a meeting room (select_page_item type:'room' id:<roomId>)",
      'navigate to another section (navigate_to_app)'
    ]
  },
  {
    // POB getting-started (S9): the pages the workspace ai's first-thread
    // offers open, as the route or in the PageSlot. Their rows are the page's
    // own form (names found on the site, the e-mails the owner types, the
    // import steps), not root collections, so the page tells the model where
    // the person is and carries no rows. The `page` names are the pages' own
    // English titles; the chip reads the same title keys.
    test: (p) => p.startsWith('/getting-started/team'),
    page: 'Invite your team',
    data: () => ({}),
    actions: ['navigate to another section (navigate_to_app)']
  },
  {
    test: (p) => p.startsWith('/getting-started/imports'),
    page: 'Import your data',
    data: () => ({}),
    actions: ['navigate to another section (navigate_to_app)']
  },
  {
    // Three built-in pages the W27 dev check found unnamed (www-ee,
    // 2026-10-31): each chip read its segment-cased first segment, English in a
    // Georgian session. Their rows are the page's own lists (the requests, the
    // map's points, the one shared item), not root collections, so each tells
    // the model where the person is and carries no rows. The chip reads a title
    // key the page already ships (aiAwarenessLabels.js). The request views under
    // the route (/inbound, /outbound) are the same requests.
    test: (p) => /^\/resolutions\/requests(?:[/?#]|$)/.test(p),
    page: 'Customer requests',
    data: () => ({}),
    actions: ['navigate to another section (navigate_to_app)']
  },
  {
    // One map page (analyticsMap) under the workspace and the project analytics.
    test: (p) => /^\/(?:workspace-)?analytics\/map(?:[/?#]|$)/.test(p),
    page: 'Analytics map',
    data: () => ({}),
    actions: ['navigate to another section (navigate_to_app)']
  },
  {
    // The public share viewer, /s/<token>. It is chromeless (no composer), but
    // a signed-in visit still derives the chips, and a token is not a name.
    test: (p) => /^\/s\/[^/?#]+/.test(p),
    page: 'Shared item',
    data: () => ({}),
    actions: ['navigate to another section (navigate_to_app)']
  }
]

// The route registry, exported for the label gate
// (__tests__/aiContextChipLabels.test.js). It enumerates every route and every
// collection a descriptor returns, so a new entry with no row in
// aiAwarenessLabels.js fails a test instead of shipping an English chip.
export const AI_AWARENESS_PAGES = PAGES

// A ROUTE is not a TITLE: it carries a query string and a hash, and neither is
// part of the page's name. `setModuleRouteQuery` writes the open row onto the
// module route (`/vault-training?board=<id>`), so the segment-casing below —
// which split on '/' and title-cased whatever came back — rendered the FOCUS
// route chip as `Vault Training?Board=<id>`, the query string capitalised as
// if it were prose (V2, VaultOS 1.38.1, 2026-09-10; the module lane fixed the
// ATTACH chip's half of the same defect). Strip both before anything reads the
// path as a name.
const _pathOnly = (p) => str(p).split('#')[0].split('?')[0]

// Every list on the live root that names a route AND gives it a human label.
// Order is the contract:
//   1. `workspaceSettings` — the RESOLVED navbar/launcher the user is actually
//      looking at (personalisation applied). The chip should say what the tab
//      says.
//   2. the takeover module's own `configDefaults` — the module's declaration,
//      for a route the resolved lists do not carry (a page reachable only from
//      inside the module, never pinned to a tab).
//   3. each additive app's, last — same "first declaration wins" ordering
//      functions/moduleRecordRoutes.js documents, so a marketplace install can
//      never rename the workspace's own module page.
const _titleLists = (root) => {
  const out = []
  if (!root || typeof root !== 'object') return out
  const ws = root.workspaceSettings
  if (ws && typeof ws === 'object') out.push(ws.navbar, ws.apps)
  const project = root.workspaceProject
  if (project && typeof project === 'object') {
    const cd = project.configDefaults
    if (cd && typeof cd === 'object') out.push(cd.navbar, cd.apps)
  }
  if (Array.isArray(root.workspaceApps)) {
    for (const app of root.workspaceApps) {
      if (app && typeof app === 'object') out.push(app.navbar, app.apps)
    }
  }
  return out
}

// Every route the installed modules serve. A navbar entry is `{ label, path }`
// and a launcher tile is `{ title, route }` (launcherTiles.js
// normalizeLauncherTile) — both shapes are read, because a module page may be
// declared as either.
const _moduleRoutes = (root) => {
  const routes = new Set()
  if (!root || typeof root !== 'object') return routes
  const project = root.workspaceProject
  if (project && project.pages && typeof project.pages === 'object') {
    for (const k of Object.keys(project.pages)) routes.add(_pathOnly(k))
  }
  if (Array.isArray(root.workspaceApps)) {
    for (const app of root.workspaceApps) {
      if (app && Array.isArray(app.pages)) for (const k of app.pages) routes.add(_pathOnly(str(k)))
    }
  }
  // A declared record route is a module page route BY CONSTRUCTION —
  // `_normalizeRecordRoutes` validates it against the module's own `pages` at
  // load. Reading the registry too means a module whose pages stash has not
  // landed yet (or an older bundle that only published recordRoutes) still
  // resolves. This is a READ of the S1/S3 contract; the registry is not
  // changed here.
  try {
    for (const row of Object.values(moduleRecordRoutes(root))) {
      if (row && row.route) routes.add(_pathOnly(str(row.route)))
    }
  } catch (_) {
    /* registry unavailable — the page set above still answers */
  }
  return routes
}

// The name the MODULE gives this route, or '' when no installed module serves
// it. `path` must already be query/hash-free.
// The row the modules declare for EXACTLY `route` (navbar tab or launcher
// tile) → `{ label, appKey }` (both '' when none). `appKey` is the installed
// app's identity the shell stamps on the ONE row an app contributes
// (workspaceConfigs.js) — the identity the tab itself names the app by.
const _declaredRow = (root, route) => {
  for (const list of _titleLists(root)) {
    if (!Array.isArray(list)) continue
    for (const item of list) {
      if (!item || typeof item !== 'object') continue
      if (_pathOnly(str(item.path || item.route || item.href)) !== route) continue
      const label = str(item.label || item.title || item.name)
      if (label) return { label, appKey: str(item.appKey) }
    }
  }
  return { label: '', appKey: '' }
}
const _declaredLabel = (root, route) => _declaredRow(root, route).label

// The declared row for a module route, else its NEAREST named ancestor's — an
// installed app names its main page (`/realestate`) but not every route it
// serves (the CRM's unit picker opens `/realestate/pick`), and the chip fell
// to segment-casing: "Realestate" in a Georgian workspace (current-2a
// 2026-09-28). The ancestor is the app's own name — read from the module,
// never a shell table row per installed app.
const _moduleRouteRow = (root, path) => {
  const none = { label: '', appKey: '' }
  const route = _pathOnly(path)
  if (!route || route === '/') return none
  if (!_moduleRoutes(root).has(route)) return none
  const own = _declaredRow(root, route)
  if (own.label) return own
  const segs = route.split('/').filter(Boolean)
  for (let i = segs.length - 1; i > 0; i--) {
    const row = _declaredRow(root, '/' + segs.slice(0, i).join('/'))
    if (row.label) return row
  }
  return none
}

export const moduleRouteTitle = (root, path) => _moduleRouteRow(root, path).label

// THE PAGE'S OWN TITLE, read from its DEFINITION by route — the page the
// shell serves at `route` (`el.context.pages`, a module page wrapped with the
// module's own context, workspaceProjects.js _wrapModulePages), its
// `metadata.title` resolved IN that context (a title factory calls the app's
// own functions — BESSI's `bsPageTitle`) and so in the session language. The
// app's name the title carries ("შეკვეთები · BESSI") is left to the app chip:
// the page's part is the first one. '' when the shell serves no such page or
// its title needs the live page (a factory that reads the page's own state
// throws here, and reads as none).
// Lane www-9b G-2 (2026-10-28): a module page with no navbar row read its
// path segment-cased — BESSI /orders said "Orders" in a Georgian session — and
// the routed element, read during an in-app navigation, is still the PREVIOUS
// page (BESSI /alerts said „მომხმარებლები" for one page). The definition is
// keyed by the route, so it is never the page before.
// CHIP-TITLES (2026-10-31): the shell's own pages read here too (titleFromPath),
// so a route with no key of its own takes the key the router itself picks — its
// `matchRoute` (context.utils): /user/<slug> is /user/:slug — never a wildcard
// key (the shell's one wildcard is the catch-all, no page of its own; the test
// is written without its quoted key, which i18nWorkspaceExtraction's comment
// stripper reads as a block-comment opener). A shell title is pageTitle()'s
// "<page> / <brand>": " / " cuts like " · ".
const _modulePageTitle = (route, el) => {
  if (!el || typeof el.call !== 'function' || !route || route === '/') return ''
  const pages = el.context && el.context.pages
  if (!pages || typeof pages !== 'object') return ''
  let def = pages[route]
  if (!def) {
    const hit = el.call('matchRoute', route, pages)
    def = hit && hit.key && !hit.key.endsWith('*') ? pages[hit.key] : null
  }
  if (!def || typeof def !== 'object' || !def.metadata) return ''
  const own = def.context && typeof def.context === 'object' ? def.context : null
  const reader = own && own !== el.context ? Object.create(el, { context: { value: own } }) : el
  let title = ''
  try {
    title = str(reader.call('resolvePageMetadataTitle', def.metadata)).trim()
    if (_KEYISH.test(title)) title = str(reader.call('polyglot', title)).trim()
  } catch (_) {
    title = ''
  }
  if (!title || _KEYISH.test(title)) return ''
  return title.split(/\s+[·|—–/]\s+/)[0].trim()
}
export function aiModulePageTitle(path, el) {
  return _modulePageTitle(_pathOnly(path), el || this)
}

// A bundle key, as `packages/shared/__tests__/i18nTestBinding.js KEY_RE`
// defines one. Used ONLY to decide whether a declared label is safe to hand to
// a consumer that cannot resolve polyglot — see titleFromPath.
const _KEYISH = /^[a-z][A-Za-z0-9]*(\.[A-Za-z][A-Za-z0-9]*)+$/

// The page's name, from its path.
//
// `el` is optional and is the polyglot resolver: a module's declared label is
// either a bundle KEY or a literal (exactly like a widget's `title`), and
// polyglot returns a non-key verbatim, so one call resolves both. Both callers
// are `export function`s reached as `el.call('buildAiAwareness', ...)` /
// `el.call('buildPageContext', ...)`, so `this` is the element there; a direct
// import call (the unit tests, aiAssistant.js's bare `buildPageContext(root)`)
// has none, and then a KEY-shaped label is dropped rather than shipped raw —
// the model must never be told the user is on `vault.nav.rooms`.
// THE LOADING WINDOW: the workspace's modules may still be landing, so a
// route no module serves YET may be one of theirs — the full workspace record
// is not in (`workspaceReady` false: its app declarations are unknown), or it
// declares installed apps (`settings.workspaceApps`) whose runtime rows have
// not arrived (`root.workspaceApps` still unset), or a takeover module
// (`settings.workspaceModule`) whose project has not. A record that will never
// complete (`_completionUnreachable`) is not loading. MEASURED (current-2a
// 2026-09-29): the composer mounts before the apps land and derives the
// awareness chips at once, so every dev load of /realestate logged the "no
// label" warning below for a route its app names a render later.
const _modulesLanding = (root) => {
  if (!root || typeof root !== 'object') return false
  const ws = root.workspace
  if (!ws || typeof ws !== 'object' || ws._completionUnreachable) return false
  if (root.workspaceReady === false) return true
  const settings = ws.settings && typeof ws.settings === 'object' ? ws.settings : null
  if (!settings) return false
  // Installed app entries only (installedAppRefs): the records-grant row is
  // not an app and never publishes — counting it held the window open for good.
  const apps = installedAppRefs(settings)
  if (apps.length && !Array.isArray(root.workspaceApps)) return true
  return !!settings.workspaceModule && !root.workspaceProject
}

// CHIP-WAIT (2026-10-31): A PAGE THE SHELL ITSELF SERVES WAITS FOR NO MODULE.
// The loading window above held every chip empty, so /admin/teams showed no
// chip until the workspace's apps had landed — although no module can ever
// serve that address. Each module registration skips a key it finds taken
// (workspaceProjects.js: the takeover's "key collision skipped", the installed
// apps' "page collision skipped" and "extension page collision skipped"), an
// app's activation replaces only its own opening placeholder, and the router's
// matchRoute scores an exact key above any `:param` or wildcard key, so nothing
// a module adds can win that address. True when the address is EXACTLY one of
// the registry's own keys — the test the boot reveal gate makes
// (workspaceProjects.js _pathRoutable) — and the page there is no module's:
// no own `context` (_wrapModulePages and _routeTableDef give each module page
// one), not an installed app's opening placeholder (its registered marker, read
// the way pages/main.js reads it), not a custom app's (isCustomAppTile, the
// extension space). A `:param` address (/user/<slug>) still waits: a module may
// register that exact address and win it.
const _shellServedRoute = (route, el) => {
  if (!route || route === '/' || !el || typeof el.call !== 'function') return false
  const pages = el.context && el.context.pages
  if (!pages || typeof pages !== 'object') return false
  if (!Object.prototype.hasOwnProperty.call(pages, route)) return false
  const def = pages[route]
  if (!def || typeof def !== 'object') return false
  if (Object.prototype.hasOwnProperty.call(def, 'context')) return false
  if (def[Symbol.for('symbols.workspace.appOpening')]) return false
  return !el.call('isCustomAppTile', { route })
}

// `chip: true` is the AI-awareness route CHIP (a person reads it): while the
// modules land it stays EMPTY rather than show an English stand-in — except on
// a page the shell itself serves (_shellServedRoute), which names itself at
// once — and the chip re-derives when they arrive. The model wire
// (`currentPage`) still gets a page name. Neither warns in that window — the
// warning is for a route that stays unnamed once everything has loaded.
const titleFromPath = (p, root, el, { chip = false } = {}) => {
  const clean = _pathOnly(p)
  const row = _moduleRouteRow(root, clean)
  const declared = row.label
  if (declared) {
    if (el && typeof el.call === 'function') {
      // An installed app's own row (it carries `appKey`) reads the App Store's
      // name — THE store-copy resolver, the call its navbar tab makes
      // (HeaderTabs), so the chip says what the tab says (Nika 2026-10-23:
      // "app names = what a person sees in the workspace App Store"). Any
      // other declared label resolves through polyglot, as before.
      const resolved = str(
        row.appKey
          ? el.call('storeItemText', { key: row.appKey, name: declared }, 'name')
          : el.call('polyglot', declared)
      )
      if (resolved) return resolved
    }
    if (!_KEYISH.test(declared)) return declared
  }
  // A module page the navbar never named: its own title, in the session's
  // language — before the English stand-in below.
  if (row.label === '' && _moduleRoutes(root).has(clean)) {
    const own = _modulePageTitle(clean, el)
    if (own) return own
  }
  const seg = clean.split('/').filter(Boolean)[0]
  if (!seg) return shellSay(el, 'workspace.aiAware.home')
  const cased = seg.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  const landing = _modulesLanding(root)
  if (landing && !(chip && _shellServedRoute(clean, el))) return chip ? '' : cased
  // SIGNED OUT on a guest route (/login, /signup, an invitation landing): no
  // assistant is there to read a chip, so the chip stays empty and nothing
  // warns — every signed-out dev load of /login and /signup logged "no label"
  // (2026-10-21). The model wire keeps the segment name. Signed in, a
  // guest-allowed page (/marketplace, /feed) keeps its chip.
  if (
    chip &&
    root &&
    !root.currentUserId &&
    el &&
    typeof el.call === 'function' &&
    el.call('isGuestAllowedPath', clean)
  ) {
    return ''
  }
  // A page no descriptor and no app row names: its own registered title — the
  // words its tab and its pane header carry — in the session's language
  // (CHIP-TITLES, Nika 2026-10-31: every route gets an en and a ka chip title;
  // census __tests__/aiChipTitleCensus.test.js). Only the chip reads it: the
  // model wire keeps the segment name, one English name in every language.
  const own = _modulePageTitle(clean, el)
  if (own) return chip ? own : cased
  // A shell page with no title of its own while the modules land: the chip
  // waits as before — no stand-in, no warning.
  if (landing) return ''
  // No label anywhere: the route's own name, segment-cased. It reads English in
  // a Georgian workspace, so DEV says so — once per route.
  warnUnlabelledOnce('route', clean)
  return cased
}

// What the user is FOCUSED on right now — an open detail modal / editor / dialog
// and the specific record it shows. detectOpenRecord is the SINGLE detection
// ladder (most-specific first; works across pages, incl. ones without a PAGES
// descriptor like /studio, /team-and-agents), returning `{ type, id, title }`
// (+ a few prose-only extras). buildSuggestions keys chips off the record TYPE;
// detectOpenView derives the server-facing sentence from the SAME record so the
// two can't drift. Returns null when nothing modal-ish is open.
function detectOpenRecord(root, el) {
  if (!root || typeof root !== 'object') return null
  const t = root.selectedTicket
  if (t) return { type: 'ticket', id: str(t.id), title: str(t.title || t.name), number: t.number }
  if (root.showEventDetail && root.selectedEvent) {
    const e = root.selectedEvent
    return { type: 'event', id: str(e.id), title: str(e.title || e.name) }
  }
  const d = root.selectedDocument
  if (d) {
    return {
      type: 'document',
      id: str(d.id),
      title: str(d.title || d.name),
      editorMode: root.editorMode ? str(root.editorMode) : ''
    }
  }
  if (root.profileEmail) {
    const email = str(root.profileEmail)
    return { type: 'person', id: email, title: email, profile: true }
  }
  if (root.selectedPerson) {
    const p = root.selectedPerson
    return {
      type: 'person',
      id: str(p.id || p.email),
      title: nameOf(p),
      email: p.email ? str(p.email) : ''
    }
  }
  if (root._selectedAgent) {
    const a = root._selectedAgent
    return { type: 'agent', id: str(a.id), title: str(a.name || a.id) }
  }
  if (root._selectedTeamMember) {
    const m = root._selectedTeamMember
    return { type: 'teamMember', id: str(m.id || m.email), title: nameOf(m) }
  }
  if (root._createAgentOpen)
    return { type: 'createAgent', id: null, title: 'workspace.aiSuggest.record.newAgent' }
  if (root._createMemberOpen)
    return { type: 'createMember', id: null, title: 'workspace.aiSuggest.record.inviteMember' }
  if (root.selectedAnnouncement) {
    const a = root.selectedAnnouncement
    return { type: 'announcement', id: str(a.id), title: str(a.title || '') }
  }
  // A note/KB/document open in the editor. `selectedDocument` above has readers
  // and no writer (state-app.js bucket note); the editor route + selectPageItem
  // write `editorDoc` — that is the key an open note actually lives on, while
  // the editor shows it (editorOnScreen).
  if (root.editorDoc && typeof root.editorDoc === 'object' && editorOnScreen(root)) {
    const d = root.editorDoc
    return {
      type: 'document',
      id: str(d.id),
      title: str(d.title || d.name) || shellSay(el, 'workspace.aiSuggest.record.untitledNote'),
      editorMode: root.editorMode ? str(root.editorMode) : ''
    }
  }
  // The open canvas PROJECT — least specific, so it sits last: a ticket/note
  // opened over the canvas wins. Same derivation as the canvas page title
  // (canvas/pages/main.js projectTitle): data.name → data.key → the URL key.
  const proj = openCanvasProject(root)
  if (proj) return proj
  return null
}

// `/p/<key>` on the workspace-stripped path (the ONLY canvas-project shape the
// unified shell serves) or the canvas main page's own mount flag. Title comes
// from the loaded project (`root.data`) when it is the open project, else the
// key — never blank, never the shell's own project.
const CANVAS_PROJECT_RE = /^\/p\/([^/?#]+)/
// The project key the current URL opens, or null. Two shapes: the RAW
// `/w/<org>[/<ws>]/p/<key>` (the canvas page's own classifier,
// parseWorkspaceProjectPath — positional, resolver-free) and the stripped
// `/p/<key>` that effectivePath yields once the workspace handle resolver has
// eaten the prefix. Neither a bare `/w/<org>` nor any shell route matches.
function canvasProjectKey(root) {
  const raw =
    (typeof window !== 'undefined' && window.location && window.location.pathname) ||
    str(root?.currentPath)
  const ws = raw ? shellParseWorkspaceProjectPath(raw) : null
  if (ws && ws.key) return str(ws.key)
  const m = CANVAS_PROJECT_RE.exec(effectivePath(root))
  return m ? decodeURIComponent(m[1]) : null
}
function openCanvasProject(root) {
  const key = canvasProjectKey(root) || ''
  if (!key && !root._projectCanvasOpen) return null
  const data =
    root.data && typeof root.data === 'object' && !root.projectNotFound ? root.data : null
  const loadedKey = data ? str(data.key) : ''
  // Only trust root.data when it IS this project (key match) or the path
  // carries no key to compare against.
  const trusted = data && (!key || !loadedKey || loadedKey === key)
  const title = (trusted && str(data.name || data.key).trim()) || key
  if (!title) return null
  return { type: 'project', id: key || loadedKey || title, title }
}

// ── THE ALTERABLE REFERENCE (2026-08-26 per Nika "user can alter this
// reference … might want to go with generic choice") ────────────────────────
// `root._aiRefMutedFor` pins a mute to ONE record identity — the same id
// string the awareness FOCUS-2 chip carries — so the user can take the open
// record OUT of the prompt (chip click) and every derived surface follows:
// detectOpenView returns null (the wire), buildSuggestions falls back to the
// page/generic set, and the chip renders with the existing `sent: 'dropped'`
// truth ("not sent", inactive ink). Opening a DIFFERENT record
// self-invalidates the mute — a stale flag can never silently strip context
// from an unrelated surface.
const recordRefKey = (rec) => (rec ? 'record:' + str(rec.type) + ':' + str(rec.id || '') : null)
const refMuted = (root, rec) => !!(rec && root && root._aiRefMutedFor === recordRefKey(rec))

// Prose sentence for the server's system instruction — derived from the single
// detectOpenRecord ladder so the two never diverge. Byte-for-byte the sentences
// the AI resolves "this ticket / doc / person / agent" against.
// A MUTED reference returns null: the wire must carry nothing the user
// excluded, or "this ticket" would silently resolve against a record the UI
// says is not sent.
function detectOpenView(root) {
  const r = detectOpenRecord(root)
  if (!r) return null
  if (refMuted(root, r)) return null
  const { type, id, title } = r
  switch (type) {
    case 'ticket':
      return `A TICKET detail is open — ${
        r.number != null ? '#' + r.number + ' ' : ''
      }"${title}" (id ${id}). "this ticket" refers to it.`
    case 'event':
      return `An EVENT detail is open — "${title}" (id ${id}). "this event" refers to it.`
    case 'document':
      return `A DOCUMENT is open in the editor — "${title}" (id ${id})${
        r.editorMode ? `, ${r.editorMode} mode` : ''
      }. "this doc" refers to it.`
    case 'person':
      return r.profile
        ? `A PERSON profile is open for ${id}. "this person" refers to them.`
        : `A PERSON detail is open — ${title}${r.email ? ' <' + r.email + '>' : ''}.`
    case 'agent':
      return `An AGENT settings modal is open — ${title}. "this agent" refers to it.`
    case 'teamMember':
      return `A TEAM MEMBER detail is open — ${title}.`
    // MODEL-FACING, not UI copy: every case of this switch composes an English
    // sentence that goes into the assistant's context window beside the
    // page/record facts. It is never rendered.
    case 'createAgent':
      return 'The CREATE-AGENT dialog is open.'
    case 'createMember':
      return 'The INVITE-MEMBER dialog is open.'
    case 'announcement':
      return `An ANNOUNCEMENT is open — "${title}".`
    case 'project':
      return `A canvas PROJECT is open — "${title}" (key ${id}). "this project" refers to it.`
    default:
      return null
  }
}

// Contextual action suggestions ("suggested UI to apply changes on whatever is
// open") — tappable chips the client surfaces above the composer. Each dispatches
// its `prompt` to the GENERAL assistant, which already receives the page/record
// context, so the prompt says "this ticket / doc / person" and the model resolves
// it. RECORD suggestions (a modal/detail is open) win over PAGE suggestions (a
// known section with nothing focused). `on` is the record title for the chip
// subtitle. Bounded, phrased as intents — not tool calls.
const RECORD_SUGGESTIONS = {
  ticket: [
    {
      labelKey: 'workspace.aiSuggest.ticket.status',
      promptKey: 'workspace.aiSuggest.ticket.statusPrompt'
    },
    {
      labelKey: 'workspace.aiSuggest.ticket.reassign',
      promptKey: 'workspace.aiSuggest.ticket.reassignPrompt'
    },
    {
      labelKey: 'workspace.aiSuggest.ticket.summarize',
      promptKey: 'workspace.aiSuggest.ticket.summarizePrompt'
    },
    {
      labelKey: 'workspace.aiSuggest.ticket.subtasks',
      promptKey: 'workspace.aiSuggest.ticket.subtasksPrompt'
    }
  ],
  event: [
    {
      labelKey: 'workspace.aiSuggest.event.reschedule',
      promptKey: 'workspace.aiSuggest.event.reschedulePrompt'
    },
    {
      labelKey: 'workspace.aiSuggest.event.summarize',
      promptKey: 'workspace.aiSuggest.event.summarizePrompt'
    }
  ],
  document: [
    {
      labelKey: 'workspace.aiSuggest.document.summarize',
      promptKey: 'workspace.aiSuggest.document.summarizePrompt'
    },
    {
      labelKey: 'workspace.aiSuggest.document.improve',
      promptKey: 'workspace.aiSuggest.document.improvePrompt'
    },
    {
      labelKey: 'workspace.aiSuggest.document.grammar',
      promptKey: 'workspace.aiSuggest.document.grammarPrompt'
    }
  ],
  person: [
    {
      labelKey: 'workspace.aiSuggest.person.summarize',
      promptKey: 'workspace.aiSuggest.person.summarizePrompt'
    },
    {
      labelKey: 'workspace.aiSuggest.person.draftMessage',
      promptKey: 'workspace.aiSuggest.person.draftMessagePrompt'
    }
  ],
  agent: [
    {
      labelKey: 'workspace.aiSuggest.agent.refine',
      promptKey: 'workspace.aiSuggest.agent.refinePrompt'
    },
    {
      labelKey: 'workspace.aiSuggest.agent.access',
      promptKey: 'workspace.aiSuggest.agent.accessPrompt'
    }
  ],
  teamMember: [
    {
      labelKey: 'workspace.aiSuggest.teamMember.summarize',
      promptKey: 'workspace.aiSuggest.teamMember.summarizePrompt'
    }
  ],
  announcement: [
    {
      labelKey: 'workspace.aiSuggest.announcement.summarize',
      promptKey: 'workspace.aiSuggest.announcement.summarizePrompt'
    },
    {
      labelKey: 'workspace.aiSuggest.announcement.draftReply',
      promptKey: 'workspace.aiSuggest.announcement.draftReplyPrompt'
    }
  ],
  createAgent: [
    {
      labelKey: 'workspace.aiSuggest.createAgent.setup',
      promptKey: 'workspace.aiSuggest.createAgent.setupPrompt'
    }
  ],
  createMember: [
    {
      labelKey: 'workspace.aiSuggest.createMember.who',
      promptKey: 'workspace.aiSuggest.createMember.whoPrompt'
    }
  ]
}

const PAGE_SUGGESTIONS = [
  {
    test: (p) => p.startsWith('/tasks') || p.startsWith('/task/') || p.startsWith('/tickets'),
    items: [
      {
        labelKey: 'workspace.aiSuggest.tasks.create',
        promptKey: 'workspace.aiSuggest.tasks.createPrompt'
      },
      {
        labelKey: 'workspace.aiSuggest.tasks.blocked',
        promptKey: 'workspace.aiSuggest.tasks.blockedPrompt'
      },
      {
        labelKey: 'workspace.aiSuggest.tasks.board',
        promptKey: 'workspace.aiSuggest.tasks.boardPrompt'
      }
    ]
  },
  {
    test: (p) => p.startsWith('/calendar'),
    items: [
      {
        labelKey: 'workspace.aiSuggest.calendar.agenda',
        promptKey: 'workspace.aiSuggest.calendar.agendaPrompt'
      },
      {
        labelKey: 'workspace.aiSuggest.calendar.schedule',
        promptKey: 'workspace.aiSuggest.calendar.schedulePrompt'
      }
    ]
  },
  {
    test: (p) => p.startsWith('/chat'),
    items: [
      {
        labelKey: 'workspace.aiSuggest.chat.catchUp',
        promptKey: 'workspace.aiSuggest.chat.catchUpPrompt'
      }
    ]
  },
  {
    test: (p) => p.startsWith('/notes') || p.startsWith('/documents') || p.startsWith('/docs'),
    items: [
      {
        labelKey: 'workspace.aiSuggest.notes.create',
        promptKey: 'workspace.aiSuggest.notes.createPrompt'
      }
    ]
  }
]

// The generic staples — always offered LAST, in quieter ink, so the active
// item never railroads the user (2026-08-26 per Nika "user might want to go
// with generic choice"). Marked `generic: true` for the island's styling.
const GENERIC_SUGGESTIONS = [
  {
    labelKey: 'workspace.aiSuggest.generic.myPlate',
    promptKey: 'workspace.aiSuggest.generic.myPlatePrompt'
  },
  {
    labelKey: 'workspace.aiSuggest.generic.whatCanYouDo',
    promptKey: 'workspace.aiSuggest.generic.whatCanYouDoPrompt'
  }
]

// A chip carries a bundle KEY, and the LABEL + the PROMPT it dispatches are
// both resolved here — the shared render site (`packages/shared/components/
// _simone/Suggestions.js`, `text: (el, s) => s.label`) belongs to another
// package and cannot resolve a key itself. `buildSuggestions` is reached as
// `el.call('buildSuggestions', root)`, so `this` is the element; a direct
// import call (the unit tests) gets the key back verbatim, which is exactly
// what those tests pin.
const _chipText = (el, key) =>
  el && typeof el.call === 'function' ? el.call('polyglot', key) : key
const _resolveChip = (el, s) => ({
  ...s,
  label: _chipText(el, s.labelKey),
  prompt: _chipText(el, s.promptKey)
})

// buildSuggestions(root) — the contextual chip set for the current surface.
// Order tells the story: the open record's actions first (unless the user
// MUTED the reference — then the page set takes their place), the page set
// when no record claims the slot, and the generic staples always last.
// Registered via `export *` in functions/index.js so the SimoneSuggestions
// component reaches it with el.call('buildSuggestions', s.root).
export function buildSuggestions(root) {
  const el = this
  if (!root || typeof root !== 'object') return []
  const out = []
  const rec = detectOpenRecord(root, el)
  const recActive = rec && !refMuted(root, rec) && RECORD_SUGGESTIONS[rec.type]
  if (recActive) {
    out.push(
      ...RECORD_SUGGESTIONS[rec.type].map((s) => ({
        ..._resolveChip(el, s),
        on: _chipText(el, rec.title || '')
      }))
    )
  } else {
    const path = effectivePath(root)
    const pg = PAGE_SUGGESTIONS.find((p) => {
      try {
        return p.test(path)
      } catch (_) {
        return false
      }
    })
    if (pg) out.push(...pg.items.map((s) => ({ ..._resolveChip(el, s), on: '' })))
  }
  const seen = new Set(out.map((s) => s.labelKey))
  for (const g of GENERIC_SUGGESTIONS) {
    if (!seen.has(g.labelKey)) out.push({ ..._resolveChip(el, g), on: '', generic: true })
  }
  return out
}

// buildUiState(root) — the live UI + data-scope snapshot for the assistant:
// WHICH page/modal/slot/sidebar is active and WHAT the SDK + data are scoped to,
// so the model resolves "the page in the slot", "this workspace", "close the
// panel", etc. against reality instead of guessing. Compact + all-optional
// ── THE AI CONTEXT's reads (functions/aiScope.js) ────────────────────────────
// The composer's context model (DESIGN-COMPOSER-FOCUS-POPOVER-CONTEXT-INSPECT-1)
// reads the SAME surface this module reads — the route, the open record, the
// built-in page and record suggestion sets — so the context selector, the
// suggestions it offers and the wire can never disagree with page awareness.
// Plain named exports for an import, NOT registered el.call functions
// (functions/index.js lists this module's registered names one by one).
export const aiEffectivePath = (root) => effectivePath(root)
export const aiOpenRecord = (root, el) => detectOpenRecord(root, el)
export const aiRecordRefKey = (rec) => recordRefKey(rec)
export const aiRecordMuted = (root, rec) => refMuted(root, rec)
export const aiRecordSuggestionItems = (type) => RECORD_SUGGESTIONS[type] || []
export const aiPageSuggestionItems = (path) => {
  const pg = PAGE_SUGGESTIONS.find((p) => {
    try {
      return p.test(path)
    } catch (_) {
      return false
    }
  })
  return pg ? pg.items : []
}
export const aiGenericSuggestionItems = () => GENERIC_SUGGESTIONS
// A module route's own declared label (navbar tab or launcher tile) for
// EXACTLY this route, or ''.
export const aiDeclaredRouteLabel = (root, route) => _declaredLabel(root, _pathOnly(route))

// (returns null when nothing notable is open). Reads root state (the framework-
// idiomatic source) plus a couple of shell signals the state doesn't mirror (the
// <html> chat-drawer/theme attributes, the windows-plugin modal stack), each
// guarded for non-browser contexts.
const buildUiState = (root) => {
  const ui = {}

  // DATA SCOPE — the workspace + org the SDK and every read/write are pinned to.
  // The single most load-bearing context fact: whose data the AI is looking at
  // (switching workspaces changes everything the tools return).
  const ws = root.workspace
  if (ws) {
    ui.workspace = {
      id: str(ws._id || ws.id),
      name: str(ws.displayName || ws.name || ws.slug),
      slug: str(ws.slug)
    }
    const mod = ws.settings && ws.settings.workspaceModule
    if (mod && mod.owner && mod.key) ui.installedApp = str(mod.owner) + '/' + str(mod.key)
  }
  const org = root.org
  if (org) {
    ui.organization = { id: str(org.id || org._id), name: str(org.name), slug: str(org.slug) }
  }

  // A page open in the shell PageSlot — the split pane BESIDE this chat. The AI
  // can reference it ("the page in the slot") and change it via open_page_in_split.
  if (root._canvasSlot) {
    ui.pageSlot = { route: str(root._canvasSlot) }
    if (root._canvasSlotSecond) ui.pageSlot.secondPane = str(root._canvasSlotSecond)
    if (slotFullscreenActive(root)) ui.pageSlot.fullscreen = true
  }

  // Assistant drawer (this chat's own container) + header-search morph.
  if (root.assistantOpen) ui.assistant = { open: true, dock: str(root.assistantDock || 'right') }
  if (root._headerSearchOpen) ui.searchOpen = true

  // Shell chrome the root state doesn't mirror — read the <html> data-attributes
  // the shell stamps (chat drawer sidebar, forced theme). Guarded for SSR.
  if (typeof document !== 'undefined' && document.documentElement) {
    const de = document.documentElement
    if (de.getAttribute('data-chat-drawer') === 'open') ui.chatDrawer = 'open'
    const theme = de.getAttribute('data-theme')
    if (theme) ui.theme = str(theme)
  }

  // Generic modal — the @symbo.ls/windows stack top (confirm/alert/prompt/custom
  // dialogs the app-specific detectOpenView ladder doesn't cover). The plugin
  // registers its ModalRoot element on a global handle (see windows ModalRoot
  // onInit); read the live stack off it. App-record modals are already reported
  // via `openView`, so this only adds the shared-dialog signal.
  try {
    const mr = typeof globalThis !== 'undefined' && globalThis.__SMBLS_WINDOWS_MODAL_ROOT__
    const stack = mr && mr.state && mr.state.stack
    if (Array.isArray(stack) && stack.length) {
      const top = stack[stack.length - 1]
      if (top) ui.dialog = { template: str(top.template || 'custom'), title: str(top.title || '') }
    }
  } catch (_) {
    /* windows plugin absent / not yet mounted — no dialog signal */
  }

  return Object.keys(ui).length ? ui : null
}

export function buildPageContext(root) {
  if (!root || typeof root !== 'object') return null
  const path = effectivePath(root)
  const uiState = buildUiState(root)
  // Focused modal/record applies on every page (even ones without a descriptor).
  const openView = detectOpenView(root)
  const match = PAGES.find((d) => {
    try {
      return d.test(path)
    } catch (_) {
      return false
    }
  })
  if (!match) {
    // Unknown / unregistered page — still tell the AI WHERE the user is, what is
    // open, and the generic capabilities, so it never claims to be blind.
    return {
      currentPage: titleFromPath(path, root, this),
      pageData: null,
      pageActions: [
        'navigate to a section (navigate_to_app)',
        'search the workspace (open_search)'
      ],
      openView,
      uiState
    }
  }
  let data = null
  try {
    data = match.data(root)
  } catch (_) {
    data = null
  }
  return { currentPage: match.page, pageData: data, pageActions: match.actions, openView, uiState }
}

// ── AI AWARENESS — "what the AI can see" as ONE derived set (O1, opus.md) ────
//
// DEFINITION: "the AI is aware of X" means X is present in the payload the
// current Send would transmit. Not "on screen", not "selected". The set is
// DERIVED from the transmit path (this module builds dispatchContext for
// simoneAiTransport, and the composer draft bag is serialised by
// promptAttachments.buildPromptPayload) — so the UI cannot claim more than the
// wire carries, and this module and the wire cannot drift.
//
// THREE TIERS, one item shape:
//   scope    — what the AI can REACH: workspace + org the SDK is pinned to,
//              plus the workspace-wide tools. One line, never a chip.
//   focus    — what the AI is LOOKING AT (implicit): the route, the open record
//              (detectOpenRecord ladder), the bounded page rows, the canvas
//              selection. Canvas selection carries a removeVia (deselect) —
//              the one focus item the user can act on; route/record do not.
//   attached — what the user HANDED it: STRUCTURED entity refs + one canvas
//              board (root.aiAttached, spec §4.1), then the text-only legs —
//              dragged refs, dropped files/images, picked elements. Removable.
// Item: { id, kind, title, icon, source, sent, removeVia }
//   kind    element | subject | ticket | project | artboard | note | file |
//           app | page | event | person | agent | announcement | image |
//           rows | route (Nika's list maps straight onto these)
//   sent    'structured' — in the allowlisted context object the server keeps
//           'text-only'  — survives ONLY as a text line/prefix in the prompt
//                          body (canvas selection, picked elements, refs, files)
//           'truncated'  — sent, but capped (board rows past the per-page cap)
//           'dropped'    — never leaves the browser. Since image attachments
//                          ride the turn (the SDK body is {content, modelMode,
//                          targetAgentId, context, attachments}), an image is
//                          'dropped' only when the attachment caps REFUSE it.
//   removeVia — a DESCRIPTOR { fn, arg } the chip dispatches with el.call, never
//           a handler. Collapses the three hardcoded removal paths.
// `composerState` is the SECOND ARGUMENT, not a root key: the draft bag
// (draft/files/refs/images) is per-composer element state and two composers can
// be live at once. Callers pass their own.
// Reactive inputs are all flat on root — a consumer MUST `subscribeTo:
// ['_cvSelectionTick']` for the canvas selection (the selection object is a
// plain in-place mutation; the tick is the only bridge) — plus pickedElements,
// currentPath, _canvasSlot, workspace, org and the detectOpenRecord keys.
const RECORD_KIND = {
  ticket: 'ticket',
  event: 'event',
  document: 'note',
  person: 'subject',
  agent: 'agent',
  teamMember: 'subject',
  createAgent: 'agent',
  createMember: 'subject',
  announcement: 'announcement',
  project: 'project'
}
const RECORD_ICON = {
  ticket: 'checkOval',
  event: 'dateOutline',
  document: 'edit',
  person: 'user',
  agent: 'star',
  teamMember: 'user',
  createAgent: 'star',
  createMember: 'user',
  announcement: 'bell',
  project: 'grid'
}
const REF_KIND = {
  ticket: 'ticket',
  project: 'project',
  page: 'artboard',
  file: 'file',
  note: 'note',
  kb: 'note',
  event: 'event'
}
const REF_ICON = {
  ticket: 'checkOval',
  project: 'grid',
  page: 'document',
  file: 'document',
  note: 'edit',
  kb: 'star',
  event: 'dateOutline'
}
const _CV_DS_PREFIX = '__ds/'

export function buildAiAwareness(root, composerState) {
  const out = { scope: null, focus: [], attached: [] }
  if (!root || typeof root !== 'object') return out
  const st = composerState && typeof composerState === 'object' ? composerState : {}

  // SCOPE — mirrors buildUiState's workspace/organization block (the wire's
  // uiState), rendered as one line.
  const ws = root.workspace
  const org = root.org
  const wsName = ws ? str(ws.displayName || ws.name || ws.slug) : ''
  const orgName = org ? str(org.name || org.slug) : ''
  out.scope = {
    workspace: wsName || null,
    organization: orgName || null,
    line: [
      wsName ? shellSay(this, 'workspace.aiAware.scopeWorkspace').replace('{name}', wsName) : null,
      orgName ? shellSay(this, 'workspace.aiAware.scopeOrg').replace('{name}', orgName) : null,
      shellSay(this, 'workspace.aiAware.scopeTools')
    ]
      .filter(Boolean)
      .join(' · ')
  }

  // FOCUS 1 — the route (dispatchContext.currentPage): structured.
  const path = effectivePath(root)
  const match = PAGES.find((d) => {
    try {
      return d.test(path)
    } catch (_) {
      return false
    }
  })
  // On a canvas project path the open-record ladder yields the PROJECT (FOCUS 2);
  // a generic route chip there would read "P" (titleFromPath of `/p/<key>`) —
  // the labeling nit the 2026-08-18 qa pass filed. The project chip is the page.
  //
  // The chip is for a PERSON, so a known route reads its label-table key in the
  // workspace's language. `match.page` is the MODEL's name for the route
  // (buildPageContext sends it as `currentPage`) and stays English.
  const pageTitle = match
    ? routeLabel(match.page, (key) => shellSay(this, key))
    : canvasProjectKey(root)
      ? ''
      : titleFromPath(path, root, this, { chip: true })
  if (pageTitle) {
    out.focus.push({
      id: 'route:' + str(path),
      kind: 'route',
      title: pageTitle,
      icon: 'grid',
      source: 'route',
      sent: 'structured',
      removeVia: null
    })
  }

  // FOCUS 2 — the open record (detectOpenRecord ladder → dispatchContext.openView).
  // THE user-alterable reference: the item carries its refKey + muted state so
  // the chip can toggle it, and a muted reference reports the EXISTING
  // `dropped` truth — the wire (detectOpenView) really does carry nothing then.
  const rec = detectOpenRecord(root, this)
  if (rec) {
    const muted = refMuted(root, rec)
    out.focus.push({
      id: recordRefKey(rec),
      kind: RECORD_KIND[rec.type] || 'subject',
      title: rec.title || rec.type,
      icon: RECORD_ICON[rec.type] || 'document',
      source: 'record',
      sent: muted ? 'dropped' : 'structured',
      refKey: recordRefKey(rec),
      muted,
      removeVia: null
    })
  }

  // FOCUS 3 — bounded page rows (dispatchContext.pageData): structured, or
  // TRUNCATED when a root array is longer than what the descriptor caps.
  if (match) {
    let data = null
    try {
      data = match.data(root)
    } catch (_) {
      data = null
    }
    if (data && typeof data === 'object') {
      // Each collection reads its own phrase keys (aiAwarenessLabels.js):
      // "1 ticket", "25 of 30 tickets" — in the workspace's language.
      const rows = rowsSummary(data, root, (key) => shellSay(this, key), match.rootKeys)
      if (rows.title) {
        out.focus.push({
          id: 'rows:' + str(match.page),
          kind: 'rows',
          title: rows.title,
          icon: 'list',
          source: 'pageData',
          sent: rows.truncated ? 'truncated' : 'structured',
          removeVia: null
        })
      }
    }
  }

  // FOCUS 4 — canvas selection (root.canvas.selection.ids). Survives ONLY as the
  // "Selected artboards: …" prompt prefix (the server drops selectedItems).
  void root._cvSelectionTick
  const canvas = root.canvas
  const ids =
    canvas && canvas.selection && Array.isArray(canvas.selection.ids) ? canvas.selection.ids : []
  if (ids.length) {
    const pages = (root.data && root.data.pages) || {}
    for (const key of ids) {
      const isDs = typeof key === 'string' && key.indexOf(_CV_DS_PREFIX) === 0
      const isPage = key in pages
      out.focus.push({
        id: 'cv:' + str(key),
        kind: isDs ? 'app' : isPage ? 'artboard' : 'element',
        title: isDs
          ? shellSay(this, 'workspace.aiAware.designSystem').replace(
              '{name}',
              str(key)
                .slice(_CV_DS_PREFIX.length)
                .replace(/^\w/, (c) => c.toUpperCase())
            )
          : str(key),
        icon: isPage ? 'copy' : 'library',
        source: 'canvasSelection',
        sent: 'text-only',
        removeVia: { fn: 'toggleSelection', arg: key }
      })
    }
  }

  // ATTACHED — STRUCTURED (spec §4.1). What the user HANDED the assistant
  // through `el.call('attachAiRef' | 'attachAiCanvas', …)`: entity refs and at
  // most one canvas board, held on `root.aiAttached` and forwarded by
  // simoneAiTransport as `context.attached`. These are the only attachments
  // that reach the model as DATA — the server keeps them after its allowlist,
  // the orchestrator renders them as a fenced ATTACHED CONTEXT block and
  // registers each ref as a retrieval candidate — so they carry
  // `sent: 'structured'` while the numbered tiers below stay 'text-only'.
  // They come FIRST for the same reason: strongest delivery, first chip.
  //
  // `kind` here is the EntityRef TYPE verbatim (record | doc | mail | ticket |
  // file | event | channel | canvas), which widens the vocabulary listed in
  // this function's header. Nothing renders off `kind` (AiContextChip reads
  // icon/title/sent/removeVia), so the honest name beats a lossy remap onto
  // the older list.
  const attached = readAiAttached(root)
  for (const ref of attached.refs) {
    if (!ref) continue
    const key = formatEntityRef(ref)
    if (!key) continue
    out.attached.push({
      id: 'attached:' + key,
      kind: str(ref.type),
      // The producer's own name for the row ▸ the ref's resolved title ▸ the
      // KIND, as a polyglot key the chip resolves at its render site. NEVER
      // the id: '<collection>/<rowId>' is a machine string, and it is the same
      // defect as the Longbow board's "Mail thread f82cc8" card (design §3.5).
      title: str((ref.meta && ref.meta.title) || ref.title || entityRefKindKey(ref)),
      icon: entityRefIcon(ref),
      source: 'attachedRef',
      sent: 'structured',
      removeVia: { fn: 'detachAiRef', arg: key }
    })
  }
  // The attached canvas board. One per turn (attachAiCanvas REPLACES), so one
  // chip. The title says how much of the board is going, because the payload
  // is capped upstream (spec §3.8: 60 nodes, 120 connections) and a chip that
  // said only the board name would over-claim.
  if (attached.canvas) {
    const cv = attached.canvas
    const nodes = Array.isArray(cv.nodes) ? cv.nodes.length : 0
    const label = str(cv.title) || shellSay(this, 'workspace.aiAware.canvasBoard')
    out.attached.push({
      id: 'attachedCanvas:' + str(cv.projectId || '') + '/' + str(cv.boardPageId || ''),
      kind: 'canvas',
      title: !nodes
        ? label
        : nodes === 1
          ? shellSay(this, 'workspace.aiAware.canvasItemsOne').replace('{name}', label)
          : shellSay(this, 'workspace.aiAware.canvasItems')
              .replace('{name}', label)
              .replace('{count}', nodes),
      icon: 'grid',
      source: 'attachedCanvas',
      sent: 'structured',
      // `attachAiCanvas(null)` IS the detach — one function, no second name.
      removeVia: { fn: 'attachAiCanvas', arg: null }
    })
  }

  // ATTACHED 1 — dragged entity refs (buildPromptPayload → dragRefToText line).
  for (const ref of Array.isArray(st.refs) ? st.refs : []) {
    if (!ref) continue
    out.attached.push({
      id: 'ref:' + str(ref.key || ref.id || ref.href),
      kind: REF_KIND[ref.kind] || 'subject',
      title: str(ref.title || ref.href || ref.kind),
      icon: REF_ICON[ref.kind] || 'document',
      source: 'ref',
      sent: 'text-only',
      removeVia: { fn: 'removePromptAttachment', arg: { kind: 'ref', key: ref.key } }
    })
  }
  // ATTACHED 2 — dropped files ("[Attached file: …]" line only).
  for (const f of Array.isArray(st.files) ? st.files : []) {
    if (!f) continue
    out.attached.push({
      id: 'file:' + str(f.key || f.name),
      kind: 'file',
      title: str(f.name),
      icon: 'document',
      source: 'file',
      sent: 'text-only',
      removeVia: { fn: 'removePromptAttachment', arg: { kind: 'file', key: f.key } }
    })
  }
  // ATTACHED 3 — images. The BYTES now go: sendPrompt puts them on the
  // envelope as `attachments`, the transport hands them to sdk.ai, and the
  // server persists them as `image` content parts the multimodal providers
  // send as real image blocks. So a chip is 'structured' when the caps accept
  // it, and 'dropped' ONLY for one they refuse (too many / too big / an
  // unsupported format) — the honest per-image verdict, computed by the SAME
  // normalizer the send path runs, so the chip and the wire can never disagree.
  const imageList = Array.isArray(st.images) ? st.images : []
  const imageVerdicts = imageList.length ? normalizeComposerImages(imageList).decisions : []
  for (let i = 0; i < imageList.length; i++) {
    const img = imageList[i]
    if (!img) continue
    const obj = typeof img === 'object'
    const accepted = imageVerdicts.find((d) => d.index === i)?.accepted === true
    out.attached.push({
      id: 'image:' + str(obj ? img.key || img.name : 'legacy'),
      kind: 'image',
      title: obj && img.name ? str(img.name) : shellSay(this, 'workspace.aiAware.image'),
      icon: 'image',
      source: 'image',
      sent: accepted ? 'structured' : 'dropped',
      removeVia:
        obj && img.key
          ? { fn: 'removePromptAttachment', arg: { kind: 'image', key: img.key } }
          : null
    })
  }
  // ATTACHED 4 — picked elements ("[user is pointing at: …]" prefix only).
  // An INSPECTED region (the composer's Inspect & ask — functions/aiInspect.js)
  // is a picked entry with its facts on `inspect`: it rides the turn
  // STRUCTURED (`context.inspected`, the server's INSPECTED line), so its
  // chip says so and wears the inspect glyph.
  for (const p of Array.isArray(root.pickedElements) ? root.pickedElements : []) {
    if (!p) continue
    const kp = Array.isArray(p.keyPath) ? p.keyPath : []
    const region = !!(p.inspect && typeof p.inspect === 'object')
    out.attached.push({
      id: 'picked:' + str(p.pickedAt || p.key || p.tag),
      kind: region ? 'region' : 'element',
      title: kp.length
        ? kp.join(' › ')
        : str(p.component || p.key || p.tag) || shellSay(this, 'workspace.aiAware.element'),
      icon: region ? 'lnInspect' : 'cursor',
      source: 'picked',
      sent: region ? 'structured' : 'text-only',
      removeVia: p.pickedAt != null ? { fn: 'removePickedElement', arg: p.pickedAt } : null
    })
  }
  return out
}

// ── The doc-family open path (document / note / kb) ─────────────────────────
// Dev 2026-10-27 (thread 6ae11be063f259960e3fa243): "Open the 'Lead Generation
// Strategy' document" showed "Empty note", and an off-page doc never opened.

// One doc carries TWO ids: its docId (the slug, `NOTE-LEAD-GENERATION-STRATEGY`)
// and its Mongo _id. Search lists it under both, and the model opens the one it
// read — so a loaded row matches either.
const _docRowIs = (x, wanted) =>
  !!(x && wanted) && (str(x.id) === wanted || str(x._id) === wanted || str(x.docId) === wanted)

// The body the editor shows: a markdown STRING stays the string, a legacy
// blocks object stays the object — the documents surface's own open shape
// (components/Main.js `_ensureContent`). Anything else is the empty note. The
// old `{ blocks: [] }` for a string body emptied every markdown doc, and the
// editor keeps an editorDoc whose id matches the address.
const _pageDocContent = (c) => (typeof c === 'string' ? c : c && typeof c === 'object' ? c : '')

// A LOADED row is the doc only when it carries a body: a markdown string or a
// legacy blocks object, as the list fetch normalized it. A row with neither is
// a LEAN list row (the home Documents tile maps no body). Opened as it is, the
// editor showed the note empty, and Edit → Save / Back wrote '' over it (the
// editor saves what it holds, documents functions/saveNote.js). A lean row is
// read by id instead (_fetchPageDoc).
const _pageDocWithBody = (row) =>
  row && (typeof row.content === 'string' || (!!row.content && typeof row.content === 'object'))
    ? row
    : null

// A doc off the loaded list: ONE docs `get`, which takes a docId OR a Mongo _id
// (server DocService.get). Through `execute`: the shell's SDK answers EVERY
// method with a promise, `getService` included, so the old
// `getService('docs').get(id)` read `.get` off a promise, threw, and every
// off-page open answered "not found". → { row, id } or null (unknown or refused).
const _fetchPageDoc = async (wanted) => {
  try {
    const res = await shellGetSDK().execute('docs', 'get', { id: wanted })
    const row = res && typeof res === 'object' && res.data ? res.data : res
    const rid = row && typeof row === 'object' && (row.docId || row._id || row.id)
    return rid ? { row, id: str(rid) } : null
  } catch (_) {
    return null
  }
}

// WHERE a doc opens. Beside the assistant's main thread it opens in the PageSlot
// (`notesNavigate` with `{ slot: true }`): a shell navigation changes the route,
// and the main thread closes on any route change (assistantMainThreadSync), so
// "open the doc" used to close the conversation that asked for it. Elsewhere the
// slot-aware notes navigation decides as it always has: a notes pane keeps it, a
// page routes. The documents surface is folded first when it is not registered
// yet. → true when something opened.
const _openPageDoc = async (el, root, path) => {
  const fnsOf = () => (el && el.context && el.context.functions) || {}
  let fns = fnsOf()
  const beside =
    typeof fns.assistantMainThreadOpen === 'function' &&
    el.call('assistantMainThreadOpen', root) === true
  if (typeof fns.notesNavigate !== 'function' && typeof fns.ensureDocumentsContext === 'function') {
    try {
      await el.call('ensureDocumentsContext')
    } catch (_) {
      /* the fold failed — the router below, or nothing */
    }
    fns = fnsOf()
  }
  if (typeof fns.notesNavigate === 'function') {
    if (beside) el.call('notesNavigate', path, { slot: true })
    else el.call('notesNavigate', path)
    return true
  }
  // No documents surface at all: the route — never beside a main thread.
  if (beside) return false
  let node = el
  while (node && !node.routes) node = node.parent
  if (!node || typeof el.router !== 'function') return false
  el.router(path, node)
  return true
}

// el.call('selectPageItem', type, id, collection?) — open/select an item on the
// current page. `this` is the element; the state-root carries the page arrays +
// selection keys. `collection` is read by the 'record' case only. Returns a
// structured { ok, … } the agent reads. Never persists data.
export async function selectPageItem(type, id, collection) {
  const root = this?.state?.root || this?.state
  if (!root || typeof root.update !== 'function') {
    return { ok: false, error: shellSay(this, 'workspace.pageItem.noUi') }
  }
  const wanted = str(id)
  // A refusal reaches the tool row's result and the model: through the
  // element's polyglot (`_say`), the id in as text, never as a pattern.
  const notFound = (key) => ({
    ok: false,
    error: shellSay(this, key).replace('{id}', () => wanted)
  })
  const byId = (arr) => (Array.isArray(arr) ? arr.find((x) => str(x?.id) === wanted) : null)
  const docById = (arr) => (Array.isArray(arr) ? arr.find((x) => _docRowIs(x, wanted)) : null)
  // Walk up to the routes-bearing root element so this.router() can drive SPA
  // navigation from any calling element — the same pattern LinkedResources
  // ._navigateTo uses (workspace/components/LinkedResources.js). Needed so an
  // "open this record" call from one page can hand off to the record's owning
  // route (its page fetch loads the record, then its own open path runs).
  const routeEl = () => {
    let node = this
    while (node && !node.routes) node = node.parent
    return node
  }
  const onRoute = (prefix) => effectivePath(root).startsWith(prefix)
  switch (str(type)) {
    case 'ticket': {
      let t = byId(root.tickets)
      if (!t) {
        // Not in the loaded board page — freshly created elsewhere, paginated
        // out, or the user is off the board. openTicketDetailModal is a GLOBAL
        // @symbo.ls/windows layer (not board-bound), so fetch the ticket by id
        // and open it from any page instead of failing with "not on this page".
        try {
          const tSvc = await shellGetSDK().getService('tickets')
          const doc = await tSvc.get(wanted)
          const row = doc?.data || doc
          const rid = row && (row._id || row.id)
          if (rid) t = { ...row, id: rid }
        } catch (_) {}
      }
      if (!t) return notFound('workspace.pageItem.ticketNotFound')
      try {
        this.call('openTicketDetailModal', t)
      } catch (_) {
        root.update({ selectedTicket: t })
      }
      return { ok: true, opened: { type: 'ticket', id: wanted } }
    }
    case 'channel': {
      // No `get`-by-id op exists for workspaceProject.chat (only list/create/
      // update/remove), so an off-/chat call relies on /chat's own list fetch
      // to load the channel after we navigate. If we ARE on /chat and the
      // channel still isn't in the loaded list, it genuinely isn't a channel
      // in this workspace — fail honestly rather than select nothing.
      const c = byId(root.channels)
      if (onRoute('/chat') && !c) return notFound('workspace.pageItem.channelNotFound')
      const rootEl = routeEl()
      // Canonical channel open (chat/pages/index.js data-channel-link handler +
      // LinkedResources._navigateTo): select the channel, reset any open
      // thread, land on the conversation tab, mark it read. Navigate to /chat
      // so the page is mounted and its channel list fetch runs when off-route.
      root.update({ activeChannelId: wanted, activeTab: 'conversation', activeThreadRootId: null })
      if (rootEl) this.router('/chat', rootEl)
      try {
        // markRead rides the lazily-folded chat context (bundle-fold) — the
        // /chat route fold above is in flight; sequence behind the fold.
        Promise.resolve(this.call('ensureChatContext'))
          .then((ok) => {
            if (ok !== false) this.call('markRead', wanted)
          })
          .catch(() => {})
      } catch (_) {}
      return { ok: true, opened: { type: 'channel', id: wanted } }
    }
    case 'document': {
      // The unified /notes grid holds every doc-family row; a doc can also sit
      // in the legacy `notes` store.
      let d = _pageDocWithBody(docById(root.documents)) || _pageDocWithBody(docById(root.notes))
      if (!d) {
        // Off the loaded lists, or only a lean row there — one docs `get`
        // (_fetchPageDoc). Works from any page, by docId or by _id.
        const got = await _fetchPageDoc(wanted)
        if (got)
          d = {
            id: got.id,
            // `editorDoc` is rendered by the DOCUMENTS surface, so its
            // untitled fallback is that surface's copy. Reused, never
            // duplicated into `workspace.*` — the workspace manifest
            // declares `documents`, so the bundle is loaded here.
            title: got.row.title || this.call('polyglot', 'documents.editor.untitled'),
            content: _docContent(got.row.body || got.row.content)
          }
      }
      if (!d) return notFound('workspace.pageItem.documentNotFound')
      // Canonical open = DocsCard.onClick (documents/components/Main.js): seed
      // editorDoc + view mode + the editor section, then open the editor.
      // No editor reads `selectedDocument`, so the old bare update was dead.
      root.update({
        editorDoc: { id: d.id, title: d.title, content: _pageDocContent(d.content) },
        editorMode: 'view',
        activeSection: 'editor'
      })
      const shown = await _openPageDoc(
        this,
        root,
        '/notes/editor#note=' + encodeURIComponent(str(d.id))
      )
      if (!shown) return { ok: false, error: shellSay(this, 'workspace.pageItem.noUi') }
      return { ok: true, opened: { type: 'document', id: wanted } }
    }
    case 'note': {
      let n = _pageDocWithBody(docById(root.notes)) || _pageDocWithBody(docById(root.documents))
      if (!n) {
        // Off the notes lists, or only a lean row there — same off-page read
        // as 'document' (docId is
        // unique across every doc-family type, so the plain get op works
        // regardless of which discriminator the row is).
        const got = await _fetchPageDoc(wanted)
        if (got)
          n = {
            id: got.id,
            title: got.row.title || this.call('polyglot', 'documents.notes.untitled'),
            content: _docContent(got.row.body || got.row.content),
            source: got.row.source || 'member',
            author: got.row.author
          }
      }
      if (!n) return notFound('workspace.pageItem.noteNotFound')
      // Canonical open = NoteCard.onClick (documents/components/Notes.js).
      root.update({
        selectedNoteId: n.id,
        editorDoc: {
          id: n.id,
          title: n.title || this.call('polyglot', 'documents.notes.untitled'),
          content: _pageDocContent(n.content),
          source: n.source || 'member',
          author: n.author
        },
        editorMode: 'view',
        activeSection: 'editor'
      })
      const shown = await _openPageDoc(
        this,
        root,
        '/notes/editor#note=' + encodeURIComponent(str(n.id))
      )
      if (!shown) return { ok: false, error: shellSay(this, 'workspace.pageItem.noUi') }
      return { ok: true, opened: { type: 'note', id: wanted } }
    }
    case 'kb': {
      let k = _pageDocWithBody(docById(root.kbArticles))
      if (!k) {
        const got = await _fetchPageDoc(wanted)
        if (got)
          k = {
            id: got.id,
            title: got.row.title || this.call('polyglot', 'documents.kb.untitledArticle'),
            content: _docContent(got.row.body || got.row.content)
          }
      }
      if (!k) return notFound('workspace.pageItem.articleNotFound')
      // Canonical open = KbCard.onClick (documents/components/KnowledgeBase.js).
      root.update({
        editorDoc: { id: k.id, title: k.title, content: _pageDocContent(k.content), source: 'kb' },
        editorMode: 'view',
        activeSection: 'editor'
      })
      const shown = await _openPageDoc(
        this,
        root,
        '/notes/editor#kb=' + encodeURIComponent(str(k.id))
      )
      if (!shown) return { ok: false, error: shellSay(this, 'workspace.pageItem.noUi') }
      return { ok: true, opened: { type: 'kb', id: wanted } }
    }
    case 'event': {
      const e = byId(root.events)
      if (e) {
        // Global @symbo.ls/windows modal — opens over any route (mirrors the
        // ticket case). openEventDetailModal sets selectedEvent +
        // showEventDetail AND mounts the modal layer via openModal; the old
        // bare `root.update({ selectedEvent })` set state but opened nothing.
        this.call('openEventDetailModal', e)
        return { ok: true, opened: { type: 'event', id: wanted } }
      }
      // Off the calendar / outside the loaded ±14-day window. workspaceProject
      // .calendar has a `get` op, but its raw row needs rowToEvent (a
      // calendar-page mapper not reachable here). The calendar route's own
      // `?event=<id>` deep-link restore (hashRestoreTick) fetches + maps +
      // opens the SAME global modal, polling until events load — hand off to
      // it instead of duplicating rowToEvent.
      const rootEl = routeEl()
      if (rootEl) {
        this.router('/calendar?event=' + encodeURIComponent(wanted), rootEl)
        return { ok: true, opened: { type: 'event', id: wanted } }
      }
      return notFound('workspace.pageItem.eventNotFound')
    }
    case 'room': {
      let r = byId(root.rooms)
      if (!r) {
        // workspaceProject.meet.rooms has a `get` op — verify the room exists
        // off-route so we fail honestly instead of joining an empty call. The
        // raw row is only an existence flag; the /meet page fetch maps + shows
        // it after navigation.
        try {
          const res = await shellGetSDK().execute('workspaceProject.meet.rooms', 'get', {
            id: wanted
          })
          const row = Array.isArray(res?.data) ? res.data[0] : (res?.data ?? res)
          if (row && (row.id || row._id)) r = row
        } catch (_) {}
      }
      if (!r) return notFound('workspace.pageItem.roomNotFound')
      // OPENING A ROOM IS NEVER A JOIN (MEET-AUTOJOIN-1). This case used to
      // call connectCall — microphone on, no preview — and select_page_item
      // is a read-only tool, so a RE-OFFERED pending call (_simone/
      // pendingClientCall.js) ran it in whatever tab next read the
      // conversation: live on dev 2026-10-18 a tab joined a call nobody
      // clicked Join on. The room now opens through the meet page's own
      // guarded deep link, `#room=<id>&view=call` (meet pages/index.js
      // `_resolveMeetSeed`): a cold link opens the JOIN PREVIEW, whose "Join
      // now" is the only thing that starts media; a link to the call this tab
      // is already in shows it again (no new media).
      const rootEl = routeEl()
      const onMeet = onRoute('/meet')
      if (rootEl) this.router('/meet#room=' + encodeURIComponent(wanted) + '&view=call', rootEl)
      if (!onMeet) return { ok: true, opened: { type: 'room', id: wanted } }
      // Already on /meet: the page does not remount and a pushed hash fires no
      // hashchange, so its seed never runs — apply its decision here. The meet
      // context is folded while its page is mounted (getLivekitRoom).
      const live = this.call('getLivekitRoom')
      if (live && str(live._smblsRoomId || live.name) === wanted) {
        root.update({ activeRoomId: wanted, meetView: 'call' })
        return { ok: true, opened: { type: 'room', id: wanted } }
      }
      root.update({
        activeRoomId: wanted,
        meetView: 'list',
        showJoinPreview: true,
        joinPreviewDraft: { roomId: wanted, mic: true, cam: true }
      })
      return { ok: true, opened: { type: 'room', id: wanted }, preview: true }
    }
    case 'record': {
      // A workspace RECORD — a row of an installed app's collection:
      // select_page_item { type: 'record', id, collection: '<app>.<collection>' },
      // the catalogue id the assistant reads ('crm.crm_deals'). A record has no
      // shell page: it opens on the module page that owns its collection, found
      // through the record-route registry the installed modules declare
      // (moduleRecordRoutes.js) and opened with the shell's module opener — the
      // path a record source chip takes (ChatSourceChip: entityRefHref, then
      // openAppPage with preferSlot; a plain router call is a hard navigation
      // to a blank page on a branded host).
      const named = str(collection).trim()
      // Every refusal NAMES the record (and the collection it was asked in),
      // through the element's polyglot, both as text, never as a pattern.
      const refuse = (key) => ({
        ok: false,
        error: shellSay(this, key)
          .replace('{id}', () => wanted)
          .replace('{collection}', () => named)
      })
      if (!wanted || !named) return refuse('workspace.pageItem.recordNoCollection')
      // The registry is keyed by the collection AS STORED, and the catalogue id
      // puts the app in front: 'crm.crm_deals' is stored as 'crm_deals'. A key
      // may carry its own dot ('vault.items'), so the name is tried whole
      // first, then without its app part.
      const dot = named.indexOf('.')
      let stored = ''
      let where = null
      for (const key of dot > 0 ? [named, named.slice(dot + 1)] : [named]) {
        where = moduleRecordRoute(root, key)
        if (where) {
          stored = key.toLowerCase()
          break
        }
      }
      // No installed app opens this collection here. Nothing was looked up,
      // so the sentence says that — never that the record does not exist.
      if (!where) return refuse('workspace.pageItem.recordNoRoute')
      // A declared record route names an app's OWN collection, which lives on
      // the records plane — one `records.get` says whether this reader can
      // open the row. ONLY that answer may call the record missing: a 404, no
      // row, or a row of another collection. Any other failure (offline, a
      // server error) opens the page anyway, and the app's page says what it
      // finds; the shell claims nothing it did not see. The shared NULL client
      // (no backend on this host) answers null to everything, so it is never
      // asked (`isNullClient`, shared/functions/sdkClient.js).
      let missing = false
      try {
        const sdk = shellGetSDK()
        if (sdk && sdk.isNullClient !== true) {
          const ws = activeWorkspaceId(root)
          const row = await sdk.execute('workspaceProject.records', 'get', {
            id: wanted,
            ...(ws ? { workspaceId: ws } : {})
          })
          const inCollection =
            row && typeof row === 'object' ? str(row.collection).toLowerCase() : ''
          missing = !row || (!!inCollection && inCollection !== stored)
        }
      } catch (err) {
        missing = !!err && Number(err.status) === 404
      }
      if (missing) return refuse('workspace.pageItem.recordNotFound')
      this.call('openAppPage', where.route + '?' + where.param + '=' + encodeURIComponent(wanted), {
        preferSlot: true
      })
      return { ok: true, opened: { type: 'record', id: wanted, collection: named } }
    }
    default:
      return {
        ok: false,
        error: shellSay(this, 'workspace.pageItem.unknownType').replace('{type}', () => str(type))
      }
  }
}

// THE CAPABILITY the 'record' case above answers for. The server offers
// select_page_item type 'record' only to a shell that declares
// `capabilities.openRecord: 1` on its chat request (aib's gate); an older
// server never sends the type, and an older shell never declares it. The flag
// rides THIS function, so a host whose registered selectPageItem is not this
// one (no record case) declares nothing — assistantClientCapabilities
// (assistantRecords.js) reads it off `context.functions.selectPageItem`.
selectPageItem.opensRecords = 1
